/**
 * src/data/retiredRoutes.js
 *
 * The commerce estate, and where each piece of it now points.
 *
 * WHY THIS FILE EXISTS
 *
 * The Adaptive Growth rebrand cut the site down to one funnel. Everything that
 * used to sell a discrete product — the marketplace, the twenty product
 * landing pages, the books, the courses, the Vault, the podcast guest tiers,
 * the workshops, the Digital Stem Cell tiers and every Zylvie checkout — is
 * retired, because the only conversion path now is the Growth Application.
 *
 * These URLs are years old and are linked from ads, email, podcasts, business
 * cards and other people's websites. Deleting the routes outright would send
 * all of that to a 404. Each one instead redirects to the nearest thing that
 * still exists, so an old link lands somewhere that makes sense rather than
 * nowhere.
 *
 * WHAT THIS IS NOT
 *
 * React Router's <Navigate replace> is a client-side redirect: the browser
 * gets a 200 and the swap happens in JavaScript. Search engines do eventually
 * follow it, but it does not pass authority the way a 301 does. The real 301s
 * belong in the edge config in front of the app (see docs/REDIRECTS.md, which
 * is generated from this file) — this map is the source of truth for both, so
 * the two cannot drift.
 *
 * Free tools, written content, legal pages and the admin CMS are NOT here.
 * They survive the cut: the funnel's own routing sends Tier C applicants to
 * the free audit, the SEO report and the masterclass, so retiring those would
 * break the funnel it is meant to serve.
 */

/** Retired path -> its replacement. Keys are exact paths unless noted. */
export const RETIRED_ROUTES = {
  // The old front door. `/` was redirecting here since the Feb 2026 phase-2
  // change; it now belongs to the Adaptive Growth homepage.
  "/marketplace": "/",

  // Website and funnel packages. Both were tiered price grids; the equivalent
  // capacity is now bought as GrowthPoints.
  "/websites": "/growth-points",
  "/funnels": "/growth-points",

  // Productised services.
  "/unlimited-design-partner": "/growth-points",
  "/website-revenue-regeneration": "/growth-points",
  "/complete-brand-identity-system": "/growth-points",
  "/gold-tie-program": "/growth-points",
  "/workshop": "/growth-points",
  "/brand-transformation-workshop": "/growth-points",

  // Books and courses. No self-serve catalogue survives, so these land on the
  // resources the funnel still offers rather than on a sales page.
  "/brand-identity-ebook": "/resources",
  "/tie-up-loose-ends": "/resources",
  "/seo-course": "/resources",
  "/seo-course/learn": "/resources",
  "/bow-tie-signal": "/resources",
  "/bow-tie-signal/learn": "/resources",
  "/social-media-virality": "/resources",
  "/social-media-uncensored": "/resources",

  // The podcast guest tiers were a paid feature ladder.
  "/the-hustle-zone": "/resources",
  "/the-hustle-zone/free-application": "/apply",
  "/the-hustle-zone/standard-guest-feature": "/apply",
  "/the-hustle-zone/premium-story-spotlight": "/apply",
  "/the-hustle-zone/vip-transformation-feature": "/apply",

  // Paid discovery calls. The Growth Allocation Call replaces all four, and
  // it is reached by application rather than by purchase.
  "/book-a-call": "/apply",
  "/book-a-call/free-consultation": "/apply",
  "/book-a-call/marketing-audit": "/apply",
  "/book-a-call/strategy-call": "/apply",
  "/book-a-call/marketing-audit-strategy-call": "/apply",

  // Checkout, licensing and delivery. Nothing is sold self-serve any more, so
  // a stale cart link goes to the application instead of a broken checkout.
  "/enroll": "/apply",
  "/vault": "/",
  "/vault/login": "/",
  "/onboarding": "/",
  "/verify-license": "/",

  // GrowthPoints post-purchase welcome. Onboarding happens on the call now.
  "/growth-points/welcome": "/growth-points",
};

/**
 * Retired path PREFIXES, for the route families that took a parameter.
 *
 * Matched with a `/*` route, so `/websites/classic-knot` and
 * `/funnels/concierge-pro` both land on the mapped target rather than each
 * tier needing its own key.
 */
export const RETIRED_PREFIXES = {
  "/websites": "/growth-points",
  "/funnels": "/growth-points",
  "/checkout": "/apply",
  "/portal": "/",
  "/creative-brief": "/",
  "/dsc": "/",
  "/webinar": "/resources",
};

/*
 * `/growth-points/:tier` is deliberately absent from both maps. The
 * GrowthPoints tier landings are retired like the rest, but `/growth-points`
 * and `/growth-points/catalog` survive underneath the same prefix, so the
 * catch has to be declared AFTER them in the router rather than as a blanket
 * prefix here. See App.js.
 */

/** Every retired path, for the sitemap generator's exclusion list. */
export const RETIRED_PATHS = Object.keys(RETIRED_ROUTES);
